import { useMemo } from 'react'
import { useBuildingStore } from '../../stores/buildingStore'
import { useRealtimeStore } from '../../stores/realtimeStore'
import { useViewStore } from '../../stores/viewStore'
import '../../styles/panels.css'

export default function StatsPanel() {
  const floors = useBuildingStore((s) => s.floors)
  const locations = useBuildingStore((s) => s.locations)
  const walls = useBuildingStore((s) => s.walls)
  const ifcWalls = useBuildingStore((s) => s.ifcWalls)
  const pathSegments = useBuildingStore((s) => s.pathSegments)
  const badges = useBuildingStore((s) => s.badges)
  const connected = useRealtimeStore((s) => s.connected)
  const lastUpdate = useRealtimeStore((s) => s.lastUpdate)
  const positions = useRealtimeStore((s) => s.positions)
  const isolatedFloor = useViewStore((s) => s.isolatedFloor)

  const presentCount = badges.filter((b) => b.status === 'present').length
  const lastUpdateStr = lastUpdate
    ? new Date(lastUpdate).toLocaleTimeString()
    : '-'

  const floorStats = useMemo(() => {
    if (isolatedFloor === null) return null

    const floorLocations = locations.filter((l) => l.floor === isolatedFloor)
    const floorBadges = badges.filter((b) => b.default_floor === isolatedFloor)
    const floorPresent = floorBadges.filter((b) => b.status === 'present').length
    const floorAbsent = floorBadges.filter((b) => b.status === 'absent').length
    const floorMeeting = floorBadges.filter((b) => b.status === 'meeting').length
    const floorMoving = floorBadges.filter((b) => b.status === 'moving').length

    // Count occupied zones: zones that have at least one badge position referencing them
    const occupiedZoneIds = new Set<string>()
    positions.forEach((pos) => {
      const matchingLocation = floorLocations.find((l) => l.name === pos.locationName)
      if (matchingLocation) {
        occupiedZoneIds.add(matchingLocation.id)
      }
    })

    return {
      totalZones: floorLocations.length,
      occupiedZones: occupiedZoneIds.size,
      present: floorPresent,
      absent: floorAbsent,
      meeting: floorMeeting,
      moving: floorMoving,
      totalBadges: floorBadges.length,
    }
  }, [isolatedFloor, locations, badges, positions])

  const floorName = isolatedFloor !== null
    ? floors.find((f) => f.index === isolatedFloor)?.name ?? `Etage ${isolatedFloor}`
    : null

  return (
    <div className="stats-panel">
      <div className="panel-section__title">Statistiques</div>

      {/* Floor-specific stats when a floor is isolated */}
      {floorStats && floorName && (
        <>
          <div className="stats-panel__floor-header">{floorName}</div>
          <ul className="stats-panel__list">
            <li>
              <span className="stats-panel__label">Presents</span>
              <span className="stats-panel__value stats-panel__value--highlight">
                {floorStats.present}
              </span>
            </li>
            <li>
              <span className="stats-panel__label">Absents</span>
              <span className="stats-panel__value">{floorStats.absent}</span>
            </li>
            <li>
              <span className="stats-panel__label">En reunion</span>
              <span className="stats-panel__value">{floorStats.meeting}</span>
            </li>
            <li>
              <span className="stats-panel__label">En deplacement</span>
              <span className="stats-panel__value">{floorStats.moving}</span>
            </li>
            <li>
              <span className="stats-panel__label">Zones occupees</span>
              <span className="stats-panel__value">
                {floorStats.occupiedZones} / {floorStats.totalZones}
              </span>
            </li>
          </ul>
          <div className="stats-panel__separator" />
        </>
      )}

      {/* Global stats */}
      <div className="panel-section__title">Global</div>
      <ul className="stats-panel__list">
        <li>
          <span className="stats-panel__label">Etages</span>
          <span className="stats-panel__value">{floors.length}</span>
        </li>
        <li>
          <span className="stats-panel__label">Zones</span>
          <span className="stats-panel__value">{locations.length}</span>
        </li>
        <li>
          <span className="stats-panel__label">Murs manuels</span>
          <span className="stats-panel__value">{walls.length}</span>
        </li>
        <li>
          <span className="stats-panel__label">Murs IFC</span>
          <span className="stats-panel__value">{ifcWalls.length}</span>
        </li>
        <li>
          <span className="stats-panel__label">Segments chemin</span>
          <span className="stats-panel__value">{pathSegments.length}</span>
        </li>
        <li>
          <span className="stats-panel__label">Occupants presents</span>
          <span className="stats-panel__value stats-panel__value--highlight">
            {presentCount} / {badges.length}
          </span>
        </li>
        <li>
          <span className="stats-panel__label">WebSocket</span>
          <span className={`stats-panel__value ${connected ? 'stats-panel__value--highlight' : 'stats-panel__value--warn'}`}>
            {connected ? 'Connecte' : 'Deconnecte'}
          </span>
        </li>
        <li>
          <span className="stats-panel__label">Positions live</span>
          <span className="stats-panel__value">{positions.size}</span>
        </li>
        <li>
          <span className="stats-panel__label">Derniere MAJ</span>
          <span className="stats-panel__value">{lastUpdateStr}</span>
        </li>
      </ul>
    </div>
  )
}
